import { CHIP_SIZE } from '@repo/design-system/index'
import { AchievementCampaignStatus, AchievementStage, Query, QuestProgress } from '@repo/graphql/graphql'
import classNames from 'classnames'
import { differenceInDays } from 'date-fns'
import { useState } from 'react'
import { useAchievementListInfo } from '../../../modules/apis/achievement/queries'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import { n_achievement } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { LOCALE } from '../challenge/types'
import AchievementCardSkeleton from './AchievementCardSkeleton'
import { NewAchievementChip, StageUnlockedChip } from './AchievementChip'
import ProgressBar from './ProgressBar'

interface IVariables {
  achievementCampaigns: IAchievementCampaign[]
}

interface IAchievementCampaign {
  id?: string
  title?: string
  status?: AchievementCampaignStatus
  startAt?: string
  iconImageUrl?: string
  completionIconImageUrl?: string
  completionGuide?: string
  myAchievementStages?: {
    stage?: AchievementStage
    progress?: QuestProgress
    completedAll?: boolean
    latestStageCompletedAt?: string
  }
}

const AchievementCard = () => {
  const { isLocaleKO } = useTranslation()
  const isMobile = useIsMobile()

  const [variables, setVariables] = useState<IVariables>()

  const { isFetching } = useAchievementListInfo(
    {
      first: 10,
      stagesLast: 1,
      myAchievementStagesLast: 2,
      locale: isLocaleKO ? LOCALE.KO : LOCALE.EN,
    },
    {
      onSuccess: (data: Query) => {
        const achievementCampaigns = data?.achievementCampaigns.edges.map((edge) => {
          const campaign = edge.node
          const myAchievementStages = campaign.myAchievementStages?.edges.slice(-1)[0]?.node
          const lastLevel = campaign.stages.edges[0]?.node.level
          const completedAll = myAchievementStages?.stage.level === lastLevel && myAchievementStages?.isCompleted
          const latestStageCompletedAt = campaign.myAchievementStages?.edges[0]?.node.completedAt

          return {
            ...campaign,
            myAchievementStages: {
              stage: myAchievementStages?.stage,
              progress: myAchievementStages?.progress,
              completedAll: completedAll,
              latestStageCompletedAt: latestStageCompletedAt,
            },
          }
        })

        setVariables((prevData) => ({
          ...prevData,
          achievementCampaigns: achievementCampaigns,
        }))
      },
      // onError: (error) => handleAxiosError(error),
    }
  )

  if (!variables) return
  const { achievementCampaigns } = variables

  if (isFetching) return <AchievementCardSkeleton />
  return (
    <>
      {achievementCampaigns.map((campaign) => {
        const { stage, progress, completedAll } = campaign.myAchievementStages

        const newCampaignDaysGap = differenceInDays(new Date(), new Date(campaign.startAt))
        const stageUnlockedDaysGap = differenceInDays(
          new Date(),
          new Date(campaign.myAchievementStages.latestStageCompletedAt)
        )

        return (
          <div key={campaign?.id} className="border-1 rounded-[8px] border-gray-200 bg-white p-16">
            <div className="flex flex-row gap-8">
              {newCampaignDaysGap >= 0 && newCampaignDaysGap <= 7 && (
                <div className="mb-[12px]">
                  <NewAchievementChip size={isMobile ? CHIP_SIZE.SM : CHIP_SIZE.MD} />
                </div>
              )}
              {stageUnlockedDaysGap >= 0 && stageUnlockedDaysGap <= 7 && (
                <div className="mb-[12px]">
                  <StageUnlockedChip size={isMobile ? CHIP_SIZE.SM : CHIP_SIZE.MD} />
                </div>
              )}
            </div>
            {campaign.status === AchievementCampaignStatus.Active ? (
              completedAll ? (
                <CompletedCampaignCard campaign={campaign} stage={stage} />
              ) : (
                <ActiveCampaignCard campaign={campaign} stage={stage} progress={progress} />
              )
            ) : (
              <UpcomingCampaignCard campaign={campaign} />
            )}
          </div>
        )
      })}
    </>
  )
}

interface ActiveCampaignCardProps {
  campaign: IAchievementCampaign
  stage: AchievementStage
  progress: QuestProgress
}

const ActiveCampaignCard = ({ campaign, stage, progress }: ActiveCampaignCardProps) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  return (
    <div className="flex flex-row gap-20">
      <div className="flex w-[80px] flex-col sm:w-[100px]">
        <img className="w-full flex-shrink-0 sm:w-[100px]" src={campaign?.iconImageUrl} alt="achievement badge image" />
        <div className="Body2-14Bd w-full flex-shrink-0 px-10 py-4 text-center">
          {t(n_achievement.stage.level(stage?.level))}
        </div>
      </div>
      <div className="flex flex-grow flex-col">
        <div className={classNames('line-clamp-1', isMobile ? 'Body1-16Bd' : 'Title2-18Bd')}>{campaign?.title}</div>
        <div className={classNames('line-clamp-2 text-gray-700', isMobile ? 'Body1-16Md' : 'Title2-18Md')}>
          {stage?.guide}
        </div>
        <div className="flex flex-grow items-end">
          <ProgressBar target={progress?.target} current={progress?.current} />
        </div>
      </div>
    </div>
  )
}

interface UpcomingCampaignCardProps {
  campaign: IAchievementCampaign
}

const UpcomingCampaignCard = ({ campaign }: UpcomingCampaignCardProps) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  return (
    <div className="flex flex-row items-center gap-20">
      <img className="w-[80px] flex-shrink-0 sm:w-[100px]" src={campaign?.iconImageUrl} alt="achievement badge image" />
      <div className="flex flex-grow flex-col gap-4">
        <div className={classNames('line-clamp-1', isMobile ? 'Body1-16Bd' : 'Title2-18Bd')}>
          {t(n_achievement.coming_soon.title)}
        </div>
        <div className={classNames('line-clamp-2 text-gray-700', isMobile ? 'Body1-16Md' : 'Title2-18Md')}>
          {t(n_achievement.coming_soon.desc)}
        </div>
      </div>
    </div>
  )
}

interface CompletedCampaignCardProps {
  campaign: IAchievementCampaign
  stage: AchievementStage
}

const CompletedCampaignCard = ({ campaign, stage }: CompletedCampaignCardProps) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  return (
    <div className="flex flex-row gap-20">
      <div className="flex w-[80px] flex-col sm:w-[100px]">
        <img
          className="w-full flex-shrink-0 sm:w-[100px]"
          src={campaign?.completionIconImageUrl}
          alt="completed achievement badge image"
        />
        <div className="Body2-14Bd w-full flex-shrink-0 px-10 py-4 text-center text-yellow-500">
          {t(n_achievement.stage.level(stage?.level))}
        </div>
      </div>
      <div className="flex flex-col justify-center gap-4">
        <div className={classNames('line-clamp-1', isMobile ? 'Body1-16Bd' : 'Title2-18Bd')}>{campaign?.title}</div>
        <div className={classNames('line-clamp-2 text-gray-700', isMobile ? 'Body1-16Md' : 'Title2-18Md')}>
          {campaign?.completionGuide}
        </div>
      </div>
    </div>
  )
}

export default AchievementCard
